import { Component } from "@angular/core";
import { ExcelExportParams, ExcelStyle, GridApi, GridOptions , GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { ModalService } from "../../shared/modal.service";
import { SharedDataService } from "../../services/sharedDataService";
import { getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { PfFfpGrowthFormulasColumnDefinition } from "./ag-grid-column-definition";
import { PfPlanDetail } from "../../generated-models/PfPlanDetail";

interface IPfFfpGrowthFormulasDashboardRow {
  ServiceTreeId: string;
  ServiceTeam: string;
  Email: string;
  ManatreeName: string;
  ComponentName: string;
  ScopeType: string;
  DistributionType: string;
  Baseline: number;
  Baseline2: number | null;
  Baseline3: number | null;
  GrowthCoefficient: number | null;
  Offset: number | null;
  ResourceType: string;
  RequiredSkus: string;
  Dependencies: string | null;
}

@Component({
  standalone: false,
  templateUrl: "./pf-ffp-growth-formulas-dashboard.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
})
export class PfFfpGrowthFormulasDashboardComponent extends GridViewBaseComponent {
  gridApi: GridApi;
  gridOptions: GridOptions;
  initPageSize: number = 15;
  keyFilterStats = "filter:pf-ffp-growth-formulas-dashboard";
  pfPlanDetails: PfPlanDetail[] = [];
  rowData: IPfFfpGrowthFormulasDashboardRow[] = [];
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  public total = {
    baseline1: 0,
    baseline2: 0,
    baseline3: 0,
    growthCoefficients: "0.00000"
  };

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    protected sharedDataService: SharedDataService,
    protected modalService: ModalService
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit() {
    super.InitAgGrid(PfFfpGrowthFormulasColumnDefinition);
  }

  onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  async refreshData() {
    this.loadingService.setLoading(true);
    try {
      this.pfPlanDetails = await this.apiService.getPfPlanDetails().toPromise();
      let teams = await this.apiService.getTeamList().toPromise();
      this.rowData = this.pfPlanDetails.map((pfPlanDetail) => {
        let team = teams.find(t => t.Oid === pfPlanDetail.ServiceTreeId);
        return {
          ServiceTreeId: pfPlanDetail.ServiceTreeId,
          ServiceTeam: team ? team.Name : "",
          Email: pfPlanDetail.Email,
          ManatreeName: pfPlanDetail.ManatreeName,
          ComponentName: pfPlanDetail.ComponentName,
          ScopeType: pfPlanDetail.ScopeType,
          DistributionType: pfPlanDetail.DistributionType,
          Baseline: pfPlanDetail.Baseline,
          Baseline2: pfPlanDetail.Baseline2,
          Baseline3: pfPlanDetail.Baseline3,
          GrowthCoefficient: pfPlanDetail.GrowthCoefficient,
          Offset: pfPlanDetail.Offset,
          ResourceType: pfPlanDetail.ResourceType,
          RequiredSkus: pfPlanDetail.RequiredSkus,
          Dependencies: pfPlanDetail.Dependencies,
        } as IPfFfpGrowthFormulasDashboardRow;
      });
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", this.rowData);
      }
      this.calculateTotal();
      this.loadingService.setLoading(false);
      super.setupFilterByLocalData();
    } catch (e) {
      this.rowData = [];
      this.loadingService.setLoading(false);
      this.notificationService.error(e);
    }
  }

  calculateTotal() {
    let baseline1Total = 0;
    let baseline2Total = 0;
    let baseline3Total = 0;
    let growthCoefficientTotal = 0.0;

    for (const pfPlanDetail of this.pfPlanDetails)
    {
      baseline1Total += pfPlanDetail.Baseline ?? 0;
      baseline2Total += pfPlanDetail.Baseline2 ?? 0;
      baseline3Total += pfPlanDetail.Baseline3 ?? 0;
      growthCoefficientTotal += pfPlanDetail.GrowthCoefficient ?? 0.0;
    }

    this.total.baseline1 = baseline1Total;
    this.total.baseline2 = baseline2Total;
    this.total.baseline3 = baseline3Total;
    this.total.growthCoefficients = growthCoefficientTotal.toFixed(5);
  }

  async triggerBackfill() {
    this.loadingService.setLoading(true);
    try {
      await this.apiService.backfillPfPlanDetails().toPromise()
      await this.refreshData()
      this.loadingService.setLoading(false);
      this.notificationService.success("Backfill operation completed successfully.");
    }
    catch (e) {
      this.loadingService.setLoading(false);
      this.notificationService.error(e);
    }
  }

  exportExcel() {
    const params = {
      exportMode: "xlsx",
      skipHeader: false,
      columnGroups: false,
      skipGroups: false,
      skipFooters: false,
      skipPinnedTop: false,
      skipPinnedBottom: false,
      allColumns: false,
      onlySelected: false,
      onlySelectedAllPages: false,
      fileName: "PF FFP Growth Formulas - " + getExportedExcelFileNameSuffix(),
      sheetName: "Approvable PF Execution Plans",
      columnKeys: this.gridApi.getAllGridColumns().filter((c) => c.getColDef().colId !== "action"),
    } as ExcelExportParams;

    this.loadingService.setLoading(true);
    this.gridApi.exportDataAsExcel(params);
    this.loadingService.setLoading(false);
  }
}
